/**
 * @file TeamTaskTable.jsx
 * @location src/components/team/TeamTaskTable.jsx
 * @role ManagerTasksPage 전체 — 상태 세그먼트[미완료|완료|전체] × 주목[안 읽음|기한 초과].
 *   2단 구조: 상단 기한 업무(상시) + 하단 예정 업무(미래 지정일 — 그날 도래 전).
 *   예정은 칩이 아니라 섹션 분리(그날 오면 자동 상단 등장). 정렬·플래시는 SortTable 분리.
 */
import { useMemo, useState } from 'react';
import { isClosed } from '../../data/constants';
import { ATTENTION_LABELS } from '../../utils/managerAttention';
import { EmptyState } from '../ui/SharedUI';
import { Icon } from '../ui/Icon';
import { SortTable } from './SortTable';
import { useIsMobile } from '../../hooks/useIsMobile';
import { TeamTaskCards } from './TeamTaskCards';

/** 상태 축 — 기본 미완료. 접이식 2섹션을 단일 토글+표로 통합. */
const STATUS_VIEWS = [
  { k: 'active', l: '미완료' },
  { k: 'done',   l: '완료' },
  { k: 'all',    l: '전체' },
];
/** 주목 축 — 안 읽음 + 기한 초과. (예정은 하단 섹션 / 검수 대기·진척 정체 폐기 razor ㉓) */
const ATTN_VIEWS = [
  { k: 'unack',   l: ATTENTION_LABELS.unack },
  { k: 'overdue', l: ATTENTION_LABELS.overdue },
];

/**
 * @param {object} props
 * @param {import('../../data/types.js').Task[]} props.filteredTasks  가시성 필터 적용된 task 배열(전 상태)
 * @param {Partial<Record<'unack'|'overdue'|'future', import('../../data/types.js').Task[]>>} [props.buckets]
 *   getAttentionBuckets 결과 — 주목 칩(unack/overdue) + 예정 섹션(future=미래 지정일)
 * @param {(task: import('../../data/types.js').Task) => void} props.handleToggle  체크박스 클릭(task 전달)
 * @param {(msg: string, type?: string) => void} props.onToast
 */
export function TeamTaskTable({ filteredTasks, buckets = {}, handleToggle, onToast, cols, hideUnackAttn, centerNotice }) {
  // 두 축은 독립 — 상태(단일 선택, 기본 미완료) × 주목(보조 토글, null=끔). 둘이 중첩(AND)으로 거른다.
  const [statusView, setStatusView] = useState('active');
  const [attn, setAttn] = useState(null);
  // cols 전달 시 SortTable 컬럼 구성 교체(멤버=check열·담당자 제거). undefined면 SortTable 기본값 사용.
  const rowProps = { handleToggle, onToast, cols };
  const isMobile = useIsMobile();
  const renderRows = (tasks) => isMobile
    ? <TeamTaskCards tasks={tasks} handleToggle={handleToggle} cols={cols} />
    : <SortTable tasks={tasks} {...rowProps} />;

  const active = useMemo(() => filteredTasks.filter(t => !isClosed(t.status)), [filteredTasks]);
  const done   = useMemo(() => filteredTasks.filter(t => isClosed(t.status)),  [filteredTasks]);

  const statusSets = { active, done, all: filteredTasks };
  const attnSets = {
    unack: buckets.unack ?? [],
    overdue: buckets.overdue ?? [],
  };

  const base = statusSets[statusView];
  const shown = useMemo(() => {
    if (!attn) return base;
    const ids = new Set((attnSets[attn] ?? []).map(t => t.id));
    return base.filter(t => ids.has(t.id));   // 상태 ∩ 주목
  }, [base, attn, buckets]);

  // 주목 칩 카운트 = 현재 상태 집합 ∩ 주목 (AND 결과 건수를 칩에 노출 — 관계 명확화)
  const attnCounts = useMemo(() => {
    const count = (set) => {
      const ids = new Set(set.map(t => t.id));
      return base.filter(t => ids.has(t.id)).length;
    };
    return { unack: count(attnSets.unack), overdue: count(attnSets.overdue) };
  }, [base, buckets]);

  // 예정업무(하단) = 미래 지정일. 주목 필터 중이거나 완료 뷰에선 분리 안 함(평면).
  const yoteiIds = useMemo(() => new Set((buckets.future ?? []).map(t => t.id)), [buckets]);
  const splitYotei = !attn && (statusView === 'active' || statusView === 'all');
  const mainRows  = splitYotei ? shown.filter(t => !yoteiIds.has(t.id)) : shown;
  const yoteiRows = splitYotei ? shown.filter(t =>  yoteiIds.has(t.id)) : [];

  const statusBtn = (v) => (
    <button
      key={v.k}
      role="tab"
      aria-selected={statusView === v.k}
      className={`seg-opt${statusView === v.k ? ' on' : ''}`}
      onClick={() => setStatusView(v.k)}
    >{v.l} <span className="seg-cnt">{statusSets[v.k].length}</span></button>
  );
  // 주목 칩 — 재클릭하면 꺼짐(null). 상태 위에 겹쳐 거른다.
  const attnBtn = (v) => {
    const on = attn === v.k;
    return (
      <button
        key={v.k}
        type="button"
        aria-pressed={on}
        className={`seg-opt${on ? ' on' : ''}`}
        onClick={() => setAttn(on ? null : v.k)}
      >{v.l} <span className="seg-cnt">{attnCounts[v.k]}</span></button>
    );
  };

  return (
    <div className="page-section">
      <div className="table-filter-bar">
        <div className="seg" role="tablist" aria-label="상태-필터포함">{STATUS_VIEWS.map(statusBtn)}</div>
        {centerNotice}
        <div className="seg attn" role="group" aria-label="주목-필터포함">
          {(hideUnackAttn ? ATTN_VIEWS.filter(v => v.k !== 'unack') : ATTN_VIEWS).map(attnBtn)}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="panel"><EmptyState icon="check" tone="positive" title="해당하는 업무가 없습니다" /></div>
      ) : (
        <>
          {/* 상단 — 기한 업무(오늘 중심·상시 표시) */}
          {mainRows.length > 0
            ? renderRows(mainRows)
            : <div className="panel panel-empty-sm">오늘 업무가 없습니다</div>}

          {/* 하단 — 예정 업무(미래 지정일, 그날 오면 상단 등장) */}
          {yoteiRows.length > 0 && (
            <div className="yotei-section">
              <div className="yotei-head">
                <Icon name="pin" size={14} /> 예정 업무
                <span className="seg-cnt">{yoteiRows.length}</span>
                <span className="yotei-hint">지정일이 오면 위쪽 업무 표로 이동합니다</span>
              </div>
              {renderRows(yoteiRows)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
